<template>
    <div class="block-editor table-create-record">
        <div class="wrapper">
            <section>
                <div class="editor-row-title">Table ID</div>
                <div class="table-selection-row">
                    <CustomPicker
                        v-model="clone.tableId"
                        :items="tables"
                        idKey="_id"
                        labelKey="name"
                        title="Select Table"
                        @change="onTableChange"
                    />
                    <SupsisButton 
                        v-if="clone.tableId" 
                        class="go-to-table-btn" 
                        @click="goToTable"
                        ghost
                    >
                        <i class="material-icons">visibility</i>
                        Tabloya Git
                    </SupsisButton>
                </div>
            </section>
            <section v-if="getSelectedTable()">
                <div class="title">Fields</div>
                <div v-for="(item, index) in clone.values" :key="item.fieldId" class="field-row">
                    <div class="field-info">
                        <i class="material-icons mr-2" v-tippy="{ content: getFieldTypeTooltip(item.fieldId) }">
                            {{ getIcon(item.fieldId) }}
                        </i>
                        <div class="field-label">{{ getFieldName(item.fieldId) }}</div>

                        <!-- Statik/Dinamik Seçimi (Sadece Checkbox ve Dropdown için) -->
                        <div v-if="['checkbox', 'dropdown'].includes(getFieldType(item.fieldId))" class="mode-switch">
                            <a-tooltip>
                                <template slot="title">
                                    Static mode: Use predefined values<br />
                                    Dynamic mode: Use variables or user input
                                </template>
                                <a-switch
                                    :checked="item.mode === 'static'"
                                    @change="onModeChange(index, $event)"
                                    size="small"
                                >
                                    <span slot="checkedChildren">Static</span>
                                    <span slot="unCheckedChildren">Dynamic</span>
                                </a-switch>
                            </a-tooltip>
                        </div>
                    </div>

                    <!-- Statik Checkbox -->
                    <a-checkbox
                        v-if="getFieldType(item.fieldId) === 'checkbox' && item.mode === 'static'"
                        :checked="item.value"
                        @change="onCheckboxChange(index, $event.target.checked)"
                    />

                    <!-- Statik Dropdown -->
                    <CustomPicker
                        v-else-if="getFieldType(item.fieldId) === 'dropdown' && item.mode === 'static'"
                        v-model="item.value"
                        :items="getDropdownOptions(item.fieldId)"
                        idKey="value"
                        labelKey="value"
                        :title="getFieldName(item.fieldId)"
                        @change="onDropdownChange(index, $event)"
                        :multiple="isDropdownMultiple(item.fieldId)"
                    />

                    <!-- Dinamik TiptapEditor -->
                    <div v-else class="text-editor-wrapper">
                        <TiptapEditor
                            :placeholder="getFieldName(item.fieldId)"
                            :show-attributes="true"
                            :show-emoji="true"
                            :max-character-count="1024"
                            v-model="clone.values[index].value"
                            automation-mode
                        />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { mapState } from "vuex";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import log2 from "../../../../../../../utils/log2";
import DropdownCell from "../../../../../Tables/detail/cells/DropdownCell.vue";
import SupsisButton from "../../../../../../../views/Pages/Settings/components/SupsisButton.vue";

const log = log2("CreateRecord");
export default {
    name: "CreateRecord",
    components: { CustomPicker, TiptapEditor, DropdownCell, SupsisButton },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value),
        };
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
    },
    watch: {
        clone: {
            deep: true,
            immediate: true,
            handler(newValue, oldValue) {
                log("watch clone changed", { newValue, oldValue });
                this.$emit("input", newValue);
            },
        },
        "clone.tableId": {
            immediate: true,
            handler(newValue, oldValue) {
                log("watch clone.tableId changed", { newValue, oldValue });
                this.initializeValues();
            },
        },
    },
    created() {
        // values dizisini oluştur
        if (!this.clone.values) {
            this.$set(this.clone, "values", []);
        }
        this.initializeValues();
    },
    methods: {
        getSelectedTable() {
            return this.tables.find((table) => table._id === this.clone.tableId);
        },
        onTableChange(tableId) {
            if (this.clone.values.length > 0) {
                this.$confirm({
                    title: "Are you sure?",
                    content: "Changing the table will clear all field values. Do you want to proceed?",
                    onOk: () => {
                        this.clone.tableId = tableId;
                        this.initializeValues();
                    },
                    onCancel: () => {
                        this.clone.tableId = this.value.tableId;
                    },
                });
            } else {
                this.clone.tableId = tableId;
                this.initializeValues();
            }
        },
        initializeValues() {
            const table = this.getSelectedTable();
            log("initializeValues", table);
            if (table) {
                // Mevcut values dizisindeki alanları al
                const existingFields = this.clone.values.map((item) => item.fieldId);
                log("initializeValues ", existingFields);
                let isChanged = false;
                // Tablodaki tüm alanları gez
                table.fields.forEach((field) => {
                    const fieldId = field._id;

                    if (!existingFields.includes(fieldId)) {
                        log("initializeValues  add field", fieldId);
                        isChanged = true;
                        const staticModes = ["checkbox", "dropdown"];
                        this.clone.values.push({
                            fieldId,
                            value: null,
                            mode: staticModes.includes(field.fieldType) ? "static" : "dynamic",
                        });
                    } else {
                        log("initializeValues  field exists", fieldId);
                    }
                });

                // Tablodan silinen alanları values dizisinden çıkar
                this.clone.values = this.clone.values.filter((item) =>
                    table.fields.some((field) => field._id === item.fieldId)
                );
            } else {
                this.clone.values = [];
            }
            this.clone = _.cloneDeep(this.clone);
        },
        getIcon(fieldId) {
            const field = this.getField(fieldId);
            log("getIcon", fieldId, field);
            if (!field) {
                return "text_fields";
            }
            switch (field.fieldType) {
                case "text":
                case "long-text":
                    return "text_fields";
                case "number":
                    return "looks_one";
                case "currency":
                    return "attach_money";
                case "datetime":
                    return "event";
                case "dropdown":
                    return "arrow_drop_down";
                case "checkbox":
                    return "check_box";
                case "url":
                    return "link";
                case "email":
                    return "email";
                case "phone":
                    return "phone";
                case "json":
                    return "code";
                case "file":
                    return "attachment";
                case "image":
                    return "image";
                case "video":
                    return "videocam";
                case "audio":
                    return "audiotrack";
                default:
                    return "";
            }
        },
        getFieldTypeTooltip(fieldId) {
            const field = this.getField(fieldId);
            return field ? `This field should be of type ${field.fieldType}. Invalid values cannot be added.` : "";
        },
        getField(fieldId) {
            return this.getSelectedTable().fields.find((field) => field._id === fieldId);
        },
        getFieldName(fieldId) {
            const field = this.getField(fieldId);
            return field ? field.name : "";
        },
        getFieldType(fieldId) {
            const field = this.getField(fieldId);
            return field ? field.fieldType : "";
        },
        getFieldValue(fieldId) {
            const item = this.getFieldItem(fieldId);
            return item ? item.value : null;
        },
        getFieldMode(fieldId) {
            const item = this.getFieldItem(fieldId);
            return item ? item.mode : "dynamic";
        },
        onCheckboxChange(index, checked) {
            this.$set(this.clone.values[index], "value", checked);
        },
        onEditorInput(fieldId, value) {
            this.updateField(fieldId, value, "dynamic");
        },
        onUpdateRecord({ fieldId, value }) {
            this.updateField(fieldId, value, "static");
        },
        updateField(fieldId, value, mode) {
            const index = this.clone.values.findIndex((item) => item.fieldId === fieldId);
            if (index !== -1) {
                this.$set(this.clone.values, index, { fieldId, value, mode });
            } else {
                this.clone.values.push({ fieldId, value, mode });
            }
        },
        onModeChange(index, checked) {
            const mode = checked ? "static" : "dynamic";

            this.$set(this.clone.values[index], "mode", mode);
            const item = this.clone.values[index];
            // Dinamik moda geçildiğinde değeri boş string yap
            if (mode === "dynamic") {
                this.$set(this.clone.values[index], "value", "");
            } else {
                const field = this.getField(item.fieldId);
                if (["checkbox", "dropdown"].includes(field.fieldType)) {
                    this.$set(this.clone.values[index], "value", null);
                }
            }
        },
        getFieldItem(fieldId) {
            return this.clone.values.find((item) => item.fieldId === fieldId);
        },
        getDropdownOptions(fieldId) {
            const field = this.getField(fieldId);
            return field.options.dropdown.options || [];
        },
        isDropdownMultiple(fieldId) {
            const field = this.getField(fieldId);
            return field.options.dropdown.multiple || false;
        },
        onDropdownChange(index, value) {
            this.$set(this.clone.values[index], "value", value);
        },
        goToTable() {
            if (this.clone.tableId) {
                this.$router.push(`/tables/t/${this.clone.tableId}`);
            }
        },
    },
};
</script>

<style lang="scss">
.block-editor.table-create-record {
    .wrapper {
        padding: 30px;
        display: flex;
        flex-direction: column;
        gap: 5px;

        .title {
            margin-bottom: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .table-selection-row {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 10px;

            .custom-picker {
                flex: 1;
            }

            .go-to-table-btn {
                flex-shrink: 0;
                display: flex;
                align-items: center;
                
                i {
                    margin-right: 5px;
                }
            }
        }

        .text-editor-wrapper {
            flex: 1;
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }
        }

        .field-row {
            display: flex;
            flex-direction: column;
            margin-bottom: 20px;

            .field-info {
                display: flex;
                align-items: center;
                margin-bottom: 5px;

                .field-label {
                    margin-right: 10px;
                    font-weight: 500;
                }

                .mode-switch {
                    margin-left: auto;
                }
            }

            .text-editor-wrapper {
                flex: 1; // Input'lar kalan alanı doldursun
            }
        }
    }
}
</style>
